/**
 * Portal + CRM shared HTTP client + auth context.
 *
 * Two separate audiences (customer vs CRM staff) live under
 * /api/portal/* and /api/crm/*. Cookies are httpOnly + SameSite=None so
 * they work across the emergent preview + production domain. All calls go
 * with `withCredentials: true` so the cookie rides along.
 */
import axios from "axios";
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const portalApi = axios.create({
  baseURL: `${API}/portal`,
  withCredentials: true,
});

export const crmApi = axios.create({
  baseURL: `${API}/crm`,
  withCredentials: true,
});

/**
 * Access tokens live 15 minutes. Without this, anyone with a page open
 * longer than that got "not authenticated" on their next action. On a 401
 * we silently exchange the 7-day refresh cookie for a fresh access token
 * and retry the original call once; if the refresh itself fails we bounce
 * to the audience's login page. Concurrent 401s share one refresh request.
 */
function attachSilentRefresh(client, loginPath) {
  let refreshing = null;
  client.interceptors.response.use(
    (res) => res,
    async (error) => {
      const config = error.config || {};
      const isAuthCall = (config.url || "").includes("/auth/");
      if (error.response?.status !== 401 || config._retried || isAuthCall) {
        throw error;
      }
      config._retried = true;
      try {
        refreshing = refreshing || client.post("/auth/refresh").finally(() => { refreshing = null; });
        await refreshing;
        return client(config);
      } catch {
        window.location.assign(loginPath);
        throw error;
      }
    },
  );
}

attachSilentRefresh(portalApi, "/portal/login");
attachSilentRefresh(crmApi, "/crm/login");

/**
 * FastAPI validation errors come back as arrays of `{msg, ...}` objects.
 * Rendering that array in JSX crashes React. Always run detail through
 * this before setState.
 */
export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Auth context factory — same shape for both audiences.
 * State: `null` = still checking, object = logged in, `false` = not logged in.
 */
function makeAuthContext(client, meEndpoint = "/auth/me", logoutEndpoint = "/auth/logout") {
  const Ctx = createContext(null);
  function Provider({ children }) {
    const [user, setUser] = useState(null);

    const refresh = useCallback(async () => {
      try {
        const { data } = await client.get(meEndpoint);
        setUser(data);
        return data;
      } catch {
        setUser(false);
        return null;
      }
    }, []);

    useEffect(() => {
      refresh();
    }, [refresh]);

    const logout = async () => {
      try {
        await client.post(logoutEndpoint);
      } catch {
        /* ignore */
      }
      setUser(false);
    };

    return <Ctx.Provider value={{ user, setUser, refresh, logout }}>{children}</Ctx.Provider>;
  }
  function useCtx() {
    return useContext(Ctx);
  }
  return { Provider, useCtx };
}

export const PortalAuth = makeAuthContext(portalApi);
export const CrmAuth = makeAuthContext(crmApi);

/**
 * Password field with a show/hide eye toggle. Shared across portal + CRM
 * login, reset and change-password forms.
 */
export function PasswordInput({ value, onChange, testId, placeholder, minLength, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        data-testid={testId}
        type={show ? "text" : "password"}
        required
        minLength={minLength}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className="mt-1 w-full border border-forest/20 rounded-md px-3 py-2 pr-10 text-forest bg-offwhite focus:outline-none focus:border-forest"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        data-testid={testId ? `${testId}-toggle` : undefined}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-forest/50 hover:text-forest"
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

/**
 * Old-password + new-password form posting to `${api}/auth/change-password`.
 * Used by the portal profile tab, the CRM header modal and both forced
 * first-login reset screens.
 */
export function ChangePasswordForm({ api, onDone, testIdPrefix = "change-pw" }) {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password changed");
      setOldPw("");
      setNewPw("");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <PasswordInput value={oldPw} onChange={setOldPw} placeholder="Current password" testId={`${testIdPrefix}-old`} autoComplete="current-password" />
      <PasswordInput value={newPw} onChange={setNewPw} placeholder="New password (min 8 chars)" minLength={8} testId={`${testIdPrefix}-new`} autoComplete="new-password" />
      <button disabled={busy} data-testid={`${testIdPrefix}-submit`} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-forest text-cream text-sm disabled:opacity-50">
        {busy && <Loader2 size={14} className="animate-spin" />} Change password
      </button>
    </form>
  );
}

/**
 * Full-screen takeover shown when `must_change_password` is set — the user
 * cannot reach any other page until they replace their temporary password.
 */
export function ForcedPasswordChange({ api, onDone, onLogout, audienceLabel, testId }) {
  return (
    <div className="min-h-screen bg-offwhite flex items-center justify-center px-4 py-16" data-testid={testId}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="eyebrow text-teal mb-2">{audienceLabel}</div>
          <h1 className="font-display text-forest text-3xl">Set a new password</h1>
          <p className="text-sm text-forest/60 mt-2">For security, replace your temporary password before continuing.</p>
        </div>
        <div className="bg-white border border-forest/15 rounded-lg p-8">
          <ChangePasswordForm api={api} onDone={onDone} testIdPrefix={`${testId}-form`} />
        </div>
        <div className="text-center mt-4">
          <button onClick={onLogout} className="text-teal text-sm underline underline-offset-4" data-testid={`${testId}-logout`}>Log out</button>
        </div>
      </div>
    </div>
  );
}
